/**
 * Resolve a human-friendly display label for a graph entity.
 *
 * The schema doesn't guarantee `name` is filled — for some entity types the
 * meaningful identifier is a different property (PhoneNumber: `e164`,
 * Vehicle: `plate`, etc.). This helper centralises that fallback chain so
 * every UI surface (sidebar, dossier, canvas) shows the same label.
 *
 * Priority for each entity type, in order:
 *   1. Type-specific properties from `TYPE_PRIORITY` below
 *   2. Generic "name-ish" properties (display_name, fullName, title, name…)
 *   3. The first 8 characters of the node id (last-resort identifier)
 *
 * Anything that resolves to "Unknown" or empty whitespace is treated as
 * unset, so an LLM that wrote `name: "Unknown"` doesn't poison the label.
 */

import type { GraphViewNode } from '@/types/graph-view'

interface EntityShape {
  id: string
  name?: string
  typeLabel?: string
  labels?: string[]
  properties?: Record<string, unknown>
  /** Some surfaces ship an already-resolved `displayName` — honour it first. */
  displayName?: string
}

/** Per-type priority list of property keys to try before the generic chain. */
const TYPE_PRIORITY: Record<string, readonly string[]> = {
  PhoneNumber: ['e164', 'number', 'phone', 'phoneNumber'],
  EmailAddress: ['address', 'email'],
  Email: ['address', 'email'],
  Vehicle: ['plate', 'registration', 'vin', 'license', 'licensePlate'],
  Person: ['full_name', 'fullName', 'name'],
  Organization: ['legal_name', 'legalName', 'name'],
  Org: ['legal_name', 'legalName', 'name'],
  Location: ['address', 'place_name', 'placeName'],
  Place: ['address', 'place_name', 'placeName'],
  Address: ['formatted', 'street', 'address'],
  Document: ['title', 'filename', 'name'],
  AttachedDocument: ['title', 'filename', 'name'],
  Recording: ['title', 'filename', 'name'],
  Photo: ['title', 'filename', 'name'],
  File: ['filename', 'title', 'name'],
  IPAddress: ['ip', 'address'],
  Domain: ['domain', 'host'],
  Url: ['url'],
  URL: ['url'],
  BankAccount: ['iban', 'account_number', 'accountNumber'],
  CreditCard: ['last4', 'pan'],
  SocialAccount: ['handle', 'username'],
  ServiceSender: ['sender_id'],
  SocialMediaPost: ['title', 'content', 'text', 'body', 'message', 'caption', 'permalink', 'url'],
  BTSStation: ['cell_id', 'cellId', 'name'],
  Finding: ['title', 'finding_timestamp', 'timestamp'],
}

const GENERIC_NAME_KEYS = [
  'display_name',
  'displayName',
  'full_name',
  'fullName',
  'title',
  'name',
  'label',
] as const

// Property keys that hold free-form content (post body, message, caption…)
// rather than a tight identifier. When a label resolves from one of these,
// we truncate so sidebar / chip surfaces don't overflow with paragraphs.
const LONG_FORM_KEYS = new Set(['content', 'text', 'body', 'message', 'caption', 'description'])

const LONG_FORM_MAX_CHARS = 60

function truncate(value: string, max: number): string {
  if (value.length <= max) return value
  return `${value.slice(0, max).trimEnd()}…`
}

/** Names like "Unknown", "N/A", "—" produced by extractors aren't useful. */
const PLACEHOLDER_NAMES = new Set(['unknown', 'n/a', 'na', '-', '—', '', 'null', 'undefined'])

function isMeaningful(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  return !PLACEHOLDER_NAMES.has(trimmed.toLowerCase())
}

function resolveTypeLabel(node: EntityShape): string | undefined {
  return node.typeLabel ?? node.labels?.[0]
}

/**
 * Standard "First Last" formatting for a Person property bag. Tolerates
 * snake_case and camelCase, returns whichever half is present, or null when
 * neither produces a meaningful value. Use this instead of inlining
 * `${firstName} ${lastName}` so the format stays consistent everywhere.
 */
export function formatPersonName(props: Record<string, unknown> | null | undefined): string | null {
  if (!props) return null
  const first = props.firstName ?? props.first_name
  const last = props.lastName ?? props.last_name
  if (isMeaningful(first) && isMeaningful(last)) return `${first.trim()} ${last.trim()}`
  if (isMeaningful(last)) return last.trim()
  if (isMeaningful(first)) return first.trim()
  return null
}

/**
 * Returns the best display label for the entity. Falls back to the truncated
 * id when nothing meaningful is available.
 */
export function getEntityDisplayLabel(node: EntityShape): string {
  if (isMeaningful(node.displayName)) return node.displayName.trim()
  if (isMeaningful(node.name)) return node.name.trim()

  const props = node.properties ?? {}

  // The schema-driven write-time stamp wins when present. ``classify()``
  // populates ``_display_name`` from the entity's ``is_display_name``
  // marker (with documented fallback chain). Anything below is a
  // safety-net for legacy nodes that haven't been restamped yet.
  const stamped = props._display_name
  if (isMeaningful(stamped)) return stamped.trim()

  const typeLabel = resolveTypeLabel(node)
  const typeKeys = (typeLabel && TYPE_PRIORITY[typeLabel]) ?? []

  for (const key of typeKeys) {
    const v = props[key]
    if (!isMeaningful(v)) continue
    // Long-form content (post body, message, caption) is truncated so it
    // reads as a snippet rather than overflowing chip / sidebar surfaces.
    return LONG_FORM_KEYS.has(key) ? truncate(v.trim(), LONG_FORM_MAX_CHARS) : v.trim()
  }

  if (typeLabel === 'Person') {
    const personName = formatPersonName(props)
    if (personName) return personName
  }
  for (const key of GENERIC_NAME_KEYS) {
    const v = props[key]
    if (isMeaningful(v)) return v.trim()
  }
  // Last-resort label. Prefer ``<EntityType> <short_id>`` over a bare
  // hex slice — the analyst sees *what* it is even when the LLM
  // produced no nameable property. Matches the documented decision in
  // docs/migrations/restamp-case-after-schema-change.md.
  const shortId = node.id.slice(0, 8)
  return typeLabel ? `${typeLabel} ${shortId}` : shortId
}

/** Convenience overload for `GraphViewNode` (the investigations view shape). */
export function getGraphViewNodeLabel(node: GraphViewNode): string {
  return getEntityDisplayLabel(node)
}

/** Convenience overload for the API `Person` shape used by hydrate / neighbour
 * responses. Apply this when projecting a hydrated node into a GraphView so
 * the canvas chip honours `_display_name` (and the rest of the fallback
 * chain) instead of falling back to "Default" when `name` is empty. */
export function getHydratedNodeLabel(node: {
  id: string
  name?: string
  labels?: string[]
  properties?: Record<string, unknown>
}): string {
  return getEntityDisplayLabel({
    id: node.id,
    name: node.name,
    labels: node.labels,
    properties: node.properties,
  })
}
